import {
  ReportApps,
  ReportDicomInitialized,
  loadReportDicomApps,
  getDwvPos,
  getImagesObjFromDwvPosition,
} from "@/components/dicom/reports/DicomReports";
import { DicomBox, get3dSlicePoints } from "@/components/dicom/DicomManager";
import {
  getDrawBox2D,
  getFiguresForModel,
  setupClippingPlane,
  getActualColor,
} from "@/components/dicom/slice.js";
import { jsPDF } from "jspdf";
// https://www.npmjs.com/package/jspdf-autotable
import { applyPlugin } from "jspdf-autotable";
import {
  getPatientData,
  getLateralSquareScreenshot,
  getFrontalSquareScreenshot,
} from "./ReportsManager";
import {
  loadAirwaysJson,
  airwaysData,
  airwaysJsonObj,
  loadAirwayDicomApps,
} from "@/components/airway/AirwayMode";
import {
  showLoader,
  hideLoader,
  delay,
} from "@/components/controls/Loader.vue";
import { getPresignedUrl, getPresignedUrls } from "@/api/reportApi";

let autoTableInitialised = false;

async function generateAirwayReport() {
  let airway =
    vueApp.airways && vueApp.airways.length ? vueApp.airways[0] : null;
  if (!airway) return;
  if (!ReportDicomInitialized) {
    await loadReportDicomApps(generateAirwayReport);
    return;
  }
  showLoader(`Preparing data`, `Please wait...`, true);

  const slices = [
    { text: "Minimal Area Slice", path: "slice_min_area.png" },
    // {
    //   text: "Minimal Area Slice + Average",
    //   path: "output_airways_lambda/airways_ellipses.png",
    // },
    { text: "Minimal AP Slice", path: "slice_min_ap.png" },
    { text: "Minimal Right-Left Slice", path: "slice_min_rl.png" },
    { text: "Bottom Slice", path: "slice_bottom.png" },
    { text: "Top Slice", path: "slice_top.png" },
  ];

  const imgLinks = [
    `${vueApp.stlBaseUrl}/saggital.png`,
    `${vueApp.stlBaseUrl}/coronal.png`,
  ];
  for (const slice of slices) {
    imgLinks.push(`${vueApp.stlBaseUrl}/${slice.path}`);
  }
  imgLinks.push(
    `${vueApp.stlBaseUrl}/output_airways_lambda/airways_ellipses.png`,
  );

  const presignedLinks = await getPresignedUrls(imgLinks);

  if (!autoTableInitialised) {
    applyPlugin(jsPDF);
    autoTableInitialised = true;
  }

  const patientData = await getPatientData();
  await loadAirwaysJson();
  console.log("generateAirwayReport", airwaysData, airwaysJsonObj);

  const pdf = new jsPDF();
  const pdfWidth = pdf.internal.pageSize.getWidth(); // 210
  const pdfHeight = pdf.internal.pageSize.getHeight(); // 297
  const textMargin = 6;

  let lineTop = addAirwayPdfHeader(pdf, patientData, textMargin);
  pdf.setFontSize(16);
  pdf.setFont("helvetica", "bold");
  let headerText = "Airway Volume Analysis";
  let headerTop = lineTop + 9;
  let dims = pdf.getTextDimensions(headerText);
  pdf.text(headerText, textMargin, headerTop);

  pdf.setFontSize(6);
  pdf.setFont("helvetica", "normal");
  let lineHeightFactor = pdf.getLineHeightFactor();
  console.log("lineHeightFactor", lineHeightFactor);
  pdf.setLineHeightFactor(0.8);

  let gender = patientData.gender;
  gender = gender.charAt(0).toUpperCase() + gender.slice(1);
  let tableTop = headerTop + textMargin / 2; // + dims.h;
  pdf.autoTable({
    startY: tableTop,
    margin: { left: textMargin, right: textMargin * 3 },
    styles: {
      halign: "left",
      lineWidth: 0,
      lineColor: [0, 0, 0],
      minCellHeight: 6,
      fontSize: 9,
      fillColor: false,
      textColor: [0, 0, 0],
      // fontStyle: "bold",
      cellPadding: 0,
    },
    headStyles: { fillColor: false, cellPadding: 0 },
    alternateRowStyles: { fillColor: false, cellPadding: 0 },
    body: [
      [
        `CephX ID: ${vueApp.patientId}`,
        `Age: ${patientData.age} Gender: ${gender}`,
        `Practitioner: `,
      ],
      [
        `First Name: ${patientData.patient_first_name}`,
        `Date of X-ray: ${patientData.cephDate}`,
        `${patientData.doctor_first_name}`,
      ],
      [
        `Last Name: ${patientData.patient_last_name}`,
        `Date of Analysis: ${patientData.analysisDate}`,
        `${patientData.doctor_last_name}`,
      ],
    ],
  });
  // console.log("table finalY", tableTop, pdf.lastAutoTable.finalY);

  let nextTopY = pdf.lastAutoTable.finalY + textMargin;

  pdf.setFontSize(14);
  pdf.setFont("helvetica", "bold");

  headerText = "Patient";
  headerTop = nextTopY;
  dims = pdf.getTextDimensions(headerText);
  pdf.text(headerText, textMargin, headerTop);

  headerText = "Average";
  pdf.text(headerText, pdfWidth / 2, headerTop);

  nextTopY = headerTop + dims.h * 1;
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(9);

  let textTop = nextTopY;
  let text = `Total airway volume: ${airwaysData.total_volume}`;
  dims = pdf.getTextDimensions(text);
  pdf.text(text, textMargin, textTop);

  let averageText = airwaysData.ellipsesInfo1 + airwaysData.ellipsesInfo2;
  // "Avg area for this size skull is 136.94 mm2. It is smaller by -42.53% of the current patient minimal area.";
  let wrapWidth = pdfWidth / 2 - textMargin * 2;
  let splitText = pdf.splitTextToSize(averageText, wrapWidth);
  pdf.setLineHeightFactor(1.4);
  pdf.text(splitText, pdfWidth / 2, textTop);
  pdf.setLineHeightFactor(lineHeightFactor);

  textTop += dims.h * 1.5;

  text = `Minimal AP diameter: ${airwaysData.slices[1].ap_diameter} mm`;
  pdf.text(text, textMargin, textTop);
  textTop += dims.h * 1.5;

  text = `Minimal RL diameter: ${airwaysData.slices[2].rl_diameter} mm`;
  pdf.text(text, textMargin, textTop);
  textTop += dims.h * 1.5;

  text = `Minimal area slice: ${airwaysData.slices[0].area} mm2`;
  pdf.text(text, textMargin, textTop);
  textTop += dims.h * 1.5;

  nextTopY = textTop + textMargin / 2;

  pdf.setFontSize(14);
  pdf.setFont("helvetica", "bold");

  headerText = "Saggital View";
  headerTop = nextTopY;
  dims = pdf.getTextDimensions(headerText);
  pdf.text(headerText, textMargin, headerTop);

  let imgTop = headerTop + dims.h - 2;
  let imgSize = pdfWidth / 2 - textMargin * 1.5;
  let imgUrl = presignedLinks[0];
  //  `${vueApp.stlBaseUrl}/saggital.png`;
  console.log("imgUrl", imgUrl);
  let imgLeft = textMargin - 1;
  pdf.addImage(imgUrl, "JPEG", imgLeft, imgTop, imgSize, imgSize);

  headerText = "Coronal View";
  pdf.text(headerText, pdfWidth / 2, headerTop);
  imgUrl = presignedLinks[1];
  //  `${vueApp.stlBaseUrl}/coronal.png`;
  imgLeft = pdfWidth / 2;
  pdf.addImage(imgUrl, "JPEG", imgLeft, imgTop, imgSize, imgSize);

  addAirwayPdfFooter(pdf);

  vueApp.allTeeth.forEach((tooth) => {
    viewer.set_opacity(tooth.id, 0.4);
  });
  vueApp.bones.forEach((item) => {
    viewer.set_opacity(item.id, 0.4);
  });

  imgTop += imgSize + textMargin / 2;
  imgLeft = textMargin - 1;
  let imgData = await getLateralSquareScreenshot();
  pdf.addImage(imgData, "JPEG", imgLeft, imgTop, imgSize, imgSize);

  imgLeft = pdfWidth / 2;
  imgData = await getFrontalSquareScreenshot();
  pdf.addImage(imgData, "JPEG", imgLeft, imgTop, imgSize, imgSize);
  await delay(100);

  pdf.addPage();
  imgSize = pdfWidth / 3 + 7;
  let defaultSliceLeft = (pdfWidth - (imgSize * 2 + textMargin * 2)) / 2; // + textMargin;
  let sliceTop = addAirwayPdfHeader(pdf, patientData, textMargin) + 10;
  let sliceLeft = defaultSliceLeft; // textMargin * 3;
  pdf.setFontSize(14);
  pdf.setFont("helvetica", "bold");

  for (const [i, slice] of slices.entries()) {
    sliceLeft = i % 2 === 0 ? defaultSliceLeft : pdfWidth / 2 + 1;
    pdf.text(slice.text, sliceLeft, sliceTop);

    imgTop = sliceTop + dims.h - 2;
    imgLeft = i % 2 === 0 ? sliceLeft - 1 : pdfWidth / 2;
    // imgUrl = `${vueApp.stlBaseUrl}/${slice.path}`;
    imgUrl = presignedLinks[i + 2];
    pdf.addImage(imgUrl, "JPEG", imgLeft, imgTop, imgSize, imgSize);

    if (i % 2 !== 0) sliceTop = imgTop + imgSize + 9;
  }

  sliceLeft = pdfWidth / 2 + 1;
  imgTop = sliceTop + dims.h - 2;
  imgLeft = pdfWidth / 2;
  pdf.text("Minimum Lateral Slice", sliceLeft, sliceTop);

  let imgObj = getSaggitalImageData();
  if (imgObj) {
    let imgRel =
      imgObj.width > imgObj.height
        ? imgSize / imgObj.width
        : imgSize / imgObj.height;
    pdf.addImage(
      imgObj.overlayImage,
      "JPEG",
      imgLeft,
      imgTop,
      imgObj.width * imgRel,
      imgObj.height * imgRel,
    );
  }

  addAirwayPdfFooter(pdf);

  imgUrl = presignedLinks[presignedLinks.length - 1];
  // `${vueApp.stlBaseUrl}/output_airways_lambda/airways_ellipses.png`;
  try {
    //   let response = await fetch(imgUrl, {
    //     method: "HEAD",
    //     cache: "no-cache",
    //   });
    //   if (response.ok) {
    if (imgUrl && imgUrl.length) {
      pdf.addPage();
      sliceTop = addAirwayPdfHeader(pdf, patientData, textMargin) + 10;
      sliceLeft = defaultSliceLeft; // textMargin * 3;
      pdf.setFontSize(14);
      pdf.setFont("helvetica", "bold");
      pdf.text("Minimal Area Slice + Average", sliceLeft, sliceTop);
      imgTop = sliceTop + dims.h - 2;
      imgLeft = sliceLeft - 1;

      imgSize = imgSize * 2 + textMargin;
      pdf.addImage(imgUrl, "JPEG", imgLeft, imgTop, imgSize, imgSize);

      addAirwayPdfFooter(pdf);
    }
  } catch (error) {}

  let name = `${vueApp.patientFirstName} ${vueApp.patientLastName}`.trim();
  pdf.save(`airway report ${name}.pdf`);

  vueApp.allTeeth.forEach((tooth) => {
    viewer.set_opacity(tooth.id, vueApp.teethOpacity);
  });
  vueApp.bones.forEach((item) => {
    viewer.set_opacity(item.id, vueApp.bonesOpacity);
  });

  hideLoader();
  // let aivueApp.airways[0]
}

function addAirwayPdfHeader(pdf, patientData, textMargin) {
  const pdfWidth = pdf.internal.pageSize.getWidth(); // 210
  const pdfHeight = pdf.internal.pageSize.getHeight(); // 297
  const dataToInclude = {
    isCompanyLogo: true,
    isPatientInfo: true,
    isCaseLink: true,
    isQrCode: true,
  };

  const lineMargin = textMargin - 1;
  const headerHeight = 22;
  const lineTop = headerHeight - 1;
  pdf.setDrawColor(55, 55, 55);
  pdf.line(lineMargin, lineTop, pdfWidth - lineMargin, lineTop);
  pdf.setDrawColor(0, 0, 0);

  const imgMargin = 2;
  let imgLeft = imgMargin * 2;
  let imgTop = imgMargin * 3;

  const logoMaxWidth = pdfWidth / 2 - imgLeft * 2;
  const logoMaxHeight = lineTop - imgMargin * 4;
  let logoHeight = logoMaxHeight;
  let logoWidth = logoHeight * 2;

  let logoUrl = "images/cephx_w.png";
  pdf.addImage(logoUrl, "JPEG", imgLeft, imgTop, logoWidth, logoHeight);

  if (
    dataToInclude.isCompanyLogo &&
    patientData.iconDataUrl &&
    patientData.iconWidth > 0 &&
    patientData.iconHeight > 0
  ) {
    const logoWidthRel = logoMaxWidth / patientData.iconWidth;
    const logoHeightRel = logoMaxHeight / patientData.iconHeight;
    const logoMinRel = Math.min(logoWidthRel, logoHeightRel);
    logoWidth = patientData.iconWidth * logoMinRel;
    logoHeight = patientData.iconHeight * logoMinRel;

    pdf.addImage(
      patientData.iconDataUrl,
      "JPEG",
      pdfWidth - imgMargin * 4 - logoWidth,
      imgTop,
      logoWidth,
      logoHeight,
    );
  }

  pdf.setFontSize(10);
  pdf.setFont("helvetica");

  logoWidth = logoMaxHeight * 2;
  // const logoRight = imgLeft + logoWidth + imgMargin * 2;
  let nextLeft = imgLeft + logoWidth + imgMargin * 2 + 1;
  if (dataToInclude.isQrCode && patientData.qrDataUrl) {
    const qrLeft = nextLeft;
    const qrTop = imgTop - 1;
    const qrSize = logoMaxHeight + 2;
    pdf.addImage(patientData.qrDataUrl, "JPEG", qrLeft, qrTop, qrSize, qrSize);

    nextLeft += qrSize + imgMargin * 4;
  }

  if (dataToInclude.isCaseLink && vueApp.shortLink) {
    let linkTop = imgTop * 2 - 1;
    let linkHintText = `3D Viewer, click on link:`;
    let dims = pdf.getTextDimensions(linkHintText);
    pdf.text(linkHintText, nextLeft, linkTop);
    pdf.setTextColor("0", "0", "256");
    pdf.textWithLink(vueApp.shortLink, nextLeft, linkTop + dims.h + 2, {
      url: vueApp.shortLink,
    });
  }

  pdf.setTextColor("0", "0", "0");
  return lineTop;
}

function addAirwayPdfFooter(pdf) {
  const pdfWidth = pdf.internal.pageSize.getWidth(); // 210
  const pdfHeight = pdf.internal.pageSize.getHeight(); // 297
  const footerText =
    "This Information is suggestive only. Any diagnosis and prescription should be the decision and sole responsibility of the doctor using this material.";
  pdf.setFontSize(7);
  pdf.setFont("helvetica", "normal");
  pdf.setTextColor(150);

  let dims = pdf.getTextDimensions(footerText);

  const footerTop = pdfHeight - dims.h - 1;
  const footerLeft = (pdfWidth - dims.w) / 2 - 3;
  pdf.text(footerText, footerLeft, footerTop);

  pdf.setTextColor("0", "0", "0");
}

function getSaggitalImageData() {
  const app = ReportApps[1];
  if (DicomBox) {
    const vPosition = DicomBox.center;
    const dwvPosition = getDwvPos(vPosition, app);
    let sliceIndex = Math.floor(app.max / 2);
    let clippingPlane = setupClippingPlane(app, DicomBox, sliceIndex);
    let sliceConstant = clippingPlane.constant;
    let drawingBox = getDrawBox2D(DicomBox, app);

    let airways = vueApp.airways[0];
    let figures = getFiguresForModel(
      airways,
      clippingPlane,
      app,
      sliceIndex,
      true,
      0,
      true,
    );
    const canvas = $(`#${app.divId}-layer-0 canvas`)[0];
    const canvasContext = canvas.getContext("2d");
    app.color = getActualColor(
      app,
      canvasContext,
      airways,
      sliceConstant,
      drawingBox,
    );

    const imgObj = getImagesObjFromDwvPosition(
      app,
      dwvPosition,
      null,
      null,
      null,
      null,
      figures,
    );
    return imgObj;
  }
}

export { generateAirwayReport };
